/**
 * Mobile-number helpers for the staff-onboarding surface.
 *
 * Auth's employee-create endpoint requires a strict E.164 number
 * (/^\+[1-9]\d{7,14}$/). The product is Iran-scoped (IRR/Toman, Jalali,
 * Asia/Tehran, the +98 sign-in prefix), so a clinic admin naturally types a
 * local mobile like `09121234567`. We normalize the common shapes to E.164
 * before sending so the natural input succeeds instead of bouncing off the
 * backend regex, and we validate client-side so a genuinely malformed number
 * gets a human message rather than the raw pattern.
 */

const E164 = /^\+[1-9]\d{7,14}$/;

export function isE164(value: string): boolean {
  return E164.test(value);
}

/**
 * Best-effort normalization of an Iranian mobile to E.164.
 *
 *  +989121234567 → +989121234567   (already international)
 *  00989121234567 → +989121234567  (00 international prefix)
 *  989121234567  → +989121234567   (country code, no plus)
 *  09121234567   → +989121234567   (national trunk 0)
 *  9121234567    → +989121234567   (bare national number)
 *
 * Spaces, dashes and Persian/Arabic-Indic digits are tolerated. Anything we
 * cannot confidently place is returned trimmed for the validator to reject.
 */
export function normalizeIranMobile(raw: string): string {
  const ascii = toAsciiDigits(raw).replace(/[\s()-]/g, '');
  if (ascii === '') return '';
  if (ascii.startsWith('+')) return ascii;
  if (ascii.startsWith('00')) return `+${ascii.slice(2)}`;
  if (ascii.startsWith('98')) return `+${ascii}`;
  if (ascii.startsWith('0')) return `+98${ascii.slice(1)}`;
  if (/^\d+$/.test(ascii)) return `+98${ascii}`;
  return ascii;
}

const DIGIT_MAP: Record<string, string> = {
  '۰': '0',
  '۱': '1',
  '۲': '2',
  '۳': '3',
  '۴': '4',
  '۵': '5',
  '۶': '6',
  '۷': '7',
  '۸': '8',
  '۹': '9',
  '٠': '0',
  '١': '1',
  '٢': '2',
  '٣': '3',
  '٤': '4',
  '٥': '5',
  '٦': '6',
  '٧': '7',
  '٨': '8',
  '٩': '9',
};

function toAsciiDigits(v: string): string {
  return v.trim().replace(/[۰-۹٠-٩]/g, (d) => DIGIT_MAP[d] ?? d);
}
